import {
  Avatar,
  Button,
  Flex,
  Layout,
  Dropdown,
  Modal,
  Form,
  Input,
} from "antd";

import Logo from "@/assets/icons/Header-logo.svg?react";

import { UserOutlined } from "@ant-design/icons";
import useAuth from "@/store/useAuth.ts";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { authQueryFactory } from "@/console/apis/auth/queryFactory";
import { gymQueryFactory } from "@/console/apis/gym/queryFactory";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const { Header } = Layout;

const CustomHeader = () => {
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: gymInfo } = useQuery(gymQueryFactory.getGymInfo());

  const { mutate: changePassword } = authQueryFactory.changePassword();
  const [form] = Form.useForm();

  const { i18n, t } = useTranslation();

  const handlePasswordChange = async (values: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    const { oldPassword, newPassword } = values;
    const confirm = window.confirm(t("common.change_password"));
    if (confirm) {
      changePassword({ oldPassword, newPassword });
      setIsModalOpen(false);
      form.resetFields();
    }
  };

  const handleLanguageChange = (checked: boolean) => {
    const newLang = checked ? "en" : "ko";
    i18n.changeLanguage(newLang);
  };

  const items = [
    {
      key: "changePassword",
      label: "Change password",
      onClick: () => setIsModalOpen(true),
    },
    {
      key: "logout",
      label: "Logout",
      onClick: () => {
        const confirm = window.confirm("Logout?");
        if (confirm) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          setAuth({} as LoginResponse);
          navigate("/login");
        }
      },
    },
  ];

  return (
    <>
      <Header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "48px",
          paddingLeft: "16px",
          backgroundColor: "#001529",
        }}
      >
        <Flex gap={12} align="center" justify="center">
          {gymInfo?.logo_image_url ? (
            <img
              src={gymInfo.logo_image_url}
              alt=" Logo"
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <Logo />
          )}
          <span className="text-white text-[18px] font-bold">
            {gymInfo?.name}
          </span>
        </Flex>

        <Flex align="center" gap={16}>
          <Button
            type={i18n.language === "en" ? "primary" : "default"}
            onClick={() => handleLanguageChange(i18n.language !== "en")}
            size="small"
          >
            {i18n.language === "en" ? "EN" : "KO"}
          </Button>
          <Dropdown menu={{ items }} placement="bottomRight">
            <Flex className="gap-2 px-3 cursor-pointer" align="center">
              <Avatar icon={<UserOutlined />} />
              <span className="text-white text-[14px]">{auth?.name}</span>
            </Flex>
          </Dropdown>
        </Flex>
      </Header>
      <Modal
        title="Change password"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handlePasswordChange}>
          <Form.Item
            name="oldPassword"
            label="Current password"
            rules={[
              { required: true, message: "Current password is required" },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New password"
            rules={[{ required: true, message: "New password is required" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm new password"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords do not match")
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Change
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomHeader;
